import { createContext, useContext } from 'react';

import { OrbitToastTone } from './components/OrbitToastBubble';

/**
 * A raised notice carries a translation KEY rather than a translated string: the host translates at
 * render time, so a notice already on screen follows a language change instead of freezing in the
 * language it was raised in, and the provider that raises it needs no `t`.
 */
export interface OrbitNoticeRequest {
  tone: OrbitToastTone;
  /** Path under the Orbit module, e.g. `toast.notSaved`. */
  messageKey: string;
  params?: Record<string, string>;
  /** Drawn ahead of the message. Both failure strings carry their own sign inline and pass none. */
  glyph?: string;
}

export interface OrbitNotice extends OrbitNoticeRequest {
  /**
   * Rises on every raise. Two identical messages are otherwise indistinguishable, and the second
   * one would inherit the first's already-running dismissal timer.
   */
  id: number;
}

export interface OrbitNoticeValue {
  notice: OrbitNotice | null;
  showNotice: (request: OrbitNoticeRequest) => void;
  dismissNotice: () => void;
  /**
   * The panel element the bubble is drawn inside. The host is MOUNTED by the provider, one level
   * above the panel, so the panel hands its own node up here and the host portals back down into
   * it; that is what makes the bubble's box the panel's box rather than the viewport's.
   */
  anchor: HTMLElement | null;
  setAnchor: (node: HTMLElement | null) => void;
}

const noop = (): void => undefined;

/**
 * Inert by default so a component that raises or reads a notice still renders outside the provider
 * — every existing Orbit suite mounts its subject without one.
 */
export const ORBIT_NOTICE_FALLBACK: OrbitNoticeValue = {
  notice: null,
  showNotice: noop,
  dismissNotice: noop,
  anchor: null,
  setAnchor: noop
};

const OrbitNoticeContext = createContext<OrbitNoticeValue>(ORBIT_NOTICE_FALLBACK);

export const useOrbitNotice = (): OrbitNoticeValue => useContext(OrbitNoticeContext);

export default OrbitNoticeContext;
